import { useState, useEffect, useRef } from 'react';

interface UseOTPCountdownOptions {
  initialSeconds?: number;
  onExpire?: () => void;
  enabled?: boolean;
}

export const useOTPCountdown = ({
  initialSeconds = 600,
  onExpire,
  enabled = true
}: UseOTPCountdownOptions = {}) => {
  const [countdown, setCountdown] = useState(0);
  const [isActive, setIsActive] = useState(false);
  
  // Usar ref para armazenar o callback mais recente e evitar stale closure
  const onExpireRef = useRef(onExpire);
  // Ref para garantir que onExpire só seja chamado uma vez
  const hasExpiredRef = useRef(false);
  // Ref para armazenar o intervalo e poder limpá-lo de dentro do callback
  const intervalRef = useRef<NodeJS.Timeout | null>(null);
  
  // Atualizar ref sempre que onExpire mudar
  useEffect(() => {
    onExpireRef.current = onExpire;
  }, [onExpire]);

  // Timer para contador regressivo
  useEffect(() => {
    // Limpar intervalo anterior se existir
    if (intervalRef.current) {
      clearInterval(intervalRef.current);
      intervalRef.current = null;
    }
    
    if (isActive && enabled) {
      // Resetar flag de expiração quando o countdown é reiniciado
      hasExpiredRef.current = false;
      
      intervalRef.current = setInterval(() => {
        setCountdown((prev) => {
          if (prev <= 1) {
            // Limpar o intervalo imediatamente para evitar múltiplas execuções
            if (intervalRef.current) {
              clearInterval(intervalRef.current);
              intervalRef.current = null;
            }
            
            // Garantir que onExpire só seja chamado uma vez
            if (!hasExpiredRef.current) {
              hasExpiredRef.current = true;
              setIsActive(false);
              // Chamar onExpire de forma segura após limpar o intervalo
              const expireCallback = onExpireRef.current;
              if (expireCallback) {
                // Usar setTimeout com 0 para garantir que seja chamado após o estado ser atualizado
                setTimeout(() => {
                  expireCallback();
                }, 0);
              }
            }
            
            return 0;
          }
          return prev - 1;
        });
      }, 1000);
    }

    return () => {
      if (intervalRef.current) {
        clearInterval(intervalRef.current);
        intervalRef.current = null;
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isActive, enabled]); // onExpireRef.current é estável e não precisa estar nas dependências

  const start = (seconds?: number) => {
    hasExpiredRef.current = false;
    setCountdown(seconds || initialSeconds);
    setIsActive(true);
  };

  const stop = () => {
    hasExpiredRef.current = false;
    setIsActive(false);
    setCountdown(0);
  };

  const reset = (seconds?: number) => {
    hasExpiredRef.current = false;
    setCountdown(seconds || initialSeconds);
    setIsActive(true);
  };

  const formatTime = (seconds: number): string => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins.toString().padStart(2, '0')}:${secs.toString().padStart(2, '0')}`;
  };

  return {
    countdown,
    isActive,
    start,
    stop,
    reset,
    formatTime: () => formatTime(countdown)
  };
};
